import { camelCase, kebabCase } from 'lodash'
import listen from 'simple-listen'

import { isProductionEnvironment } from 'utils/environment'

import BUILD_VERSION from './getBuildVersion'

// The flag is still called mixPanelEvents but it applies to segment
const isTrackingEnabled = () => window.ldClient?.variation('mix-panel-events')

let userSessionProperties = {}

function isAnalyticsEnvironment() {
  return window.analytics && isProductionEnvironment()
}

function shouldSendMetrics() {
  return isTrackingEnabled() && isAnalyticsEnvironment()
}

function setUserProperties(userId, properties) {
  userSessionProperties = {
    userId,
    ...properties
  }
}

function clearUserProperies() {
  userSessionProperties = {}
}

export function track(eventName, properties) {
  if (shouldSendMetrics()) {
    window.analytics.track(eventName, {
      timestamp: new Date().toISOString(),
      ...userSessionProperties,
      ...properties
    })
  }
}

export function trackPage(pageNameOrLocation, properties) {
  if (shouldSendMetrics()) {
    window.analytics.page(pageNameOrLocation, {
      timestamp: new Date().toISOString(),
      ...userSessionProperties,
      ...properties
    })
  }
}

export function identify(userId, properties) {
  if (isAnalyticsEnvironment()) {
    // Always identify regardless of launch darkly flag to avoid
    // missing values if LD adapter is not ready by the time this is run
    const traits = {
      ...properties,
      webappVersion: BUILD_VERSION ?? '0'
    }
    window.analytics.identify(userId, traits)
    setUserProperties(userId, properties)
  }
}

export function group(companyId, properties) {
  if (isAnalyticsEnvironment()) {
    // Always group regardless of launch darkly flag to avoid
    // missing values if LD adapter is not ready by the time this is run
    window.analytics.group(companyId, properties)
  }
}

export function resetAnalytics() {
  clearUserProperies()
  if (isAnalyticsEnvironment()) {
    window.analytics.reset()
  }
}

/*
 The following ipmlementation is an event delegation handler similar to what `autotrack.js`
  does to enable `data-track-on` and friends shortcuts for adding metrics tracking.
 */
const attrPrefix = 'data-track-'
const watchEvents = ['click', 'hover']

function getAttributeFields(element, prefix) {
  const attributes = element.attributes
  const attributeKeys = Array.from(attributes)
  const attributeFields = {}

  attributeKeys.forEach(function (attribute) {
    const name = attribute.name
    // The `on` prefix is used for event handling but isn't a field.
    if (name.includes(prefix) && name != `${prefix}on`) {
      const field = camelCase(name.slice(prefix.length))
      let value = attribute.value

      // Detects Boolean value strings.
      if (value == 'true') value = true
      if (value == 'false') value = false

      attributeFields[field] = kebabCase(value)
    }
  })

  return attributeFields
}

function formatAttributeValue(val = '') {
  // camel case then capitialize first letter
  const cameled = camelCase(val)
  return cameled.charAt(0).toUpperCase() + cameled.slice(1)
}

function domEventTrackerSetup() {
  const selector = `[${attrPrefix}on]`
  const handler = (event) => {
    const element = event.target.matches(selector)
      ? event.target
      : event.target.closest(selector)

    if (!element) {
      return
    }

    const events = element.getAttribute(`${attrPrefix}on`).split(',')

    // Ensures the type matches one of the events specified on the element.
    if (events.indexOf(event.type) < 0) return

    const attributeFields = getAttributeFields(element, attrPrefix)
    const action = formatAttributeValue(attributeFields.action)
    const value = formatAttributeValue(attributeFields.value)
    const label = formatAttributeValue(attributeFields.label)
    const category = formatAttributeValue(attributeFields.category)

    // Track Reference for google analytics
    // https://segment.com/docs/connections/destinations/catalog/google-analytics/#track
    track(action || label || value, {
      category,
      label,
      value
    })
  }

  listen(document, watchEvents, handler, true)
}

domEventTrackerSetup()
